import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { AiFillSave } from 'react-icons/ai';

import {
    QUERIES_ADMIN,
    QUERIES_REAL_STATE,
    ROUTES_ACCOUNT,
    ROUTES_ADMIN,
} from '@constants';

import { useRealState } from 'contexts/RealStateContext';

import { Button, Loading, Shadow } from 'components/atoms';
import { OptionBlock, InputText } from 'components/molecules';

import { onErrorMessage, onSuccessMessage, onValidationSchema } from 'helpers';

import {
    createAdministratorPersonTag,
    getAdministratorPersonTagShow,
    updateAdministratorPersonTag,
} from 'services';

import { PersonTagFormWrap } from './styles';
import Layout from '../../Layout';

import type { IAPersonTag } from '@types';

const PersonTagForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const queryClient = useQueryClient();

    const { getLinkWithSlug } = useRealState();

    const [step, setStep] = useState('GENERAL_INFO');

    const [nome, setNome] = useState('');
    const [slug, setSlug] = useState('');

    const titleMessages = 'Tag da Pessoa!';

    const { data: dataDetail, isLoading: isLoadingGet } = useQuery(
        [QUERIES_ADMIN.USER_TAG.base, id],
        () => getAdministratorPersonTagShow(String(id)),
        {
            enabled: !!id,
        }
    );

    useEffect(() => {
        if (dataDetail) {
            setNome(dataDetail?.nome);
            setSlug(dataDetail?.slug);
        }
    }, [dataDetail]);

    const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(
        (payload: Partial<IAPersonTag>) => createAdministratorPersonTag(payload),
        {
            mutationKey: [QUERIES_ADMIN.USER_TAG.base, id],
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Sucesso ao criar novo registro!'
                );

                queryClient.refetchQueries(QUERIES_ADMIN.USER_TAG.base);
                queryClient.refetchQueries(QUERIES_REAL_STATE.USER_TAG.list);

                navigate(getLinkWithSlug(ROUTES_ADMIN.USER_TAG.fullPath));
            },
            onError: (err) => onErrorMessage(titleMessages, err),
        }
    );

    const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(
        (payload: Partial<IAPersonTag>) => updateAdministratorPersonTag(String(id), payload),
        {
            mutationKey: [QUERIES_ADMIN.USER_TAG.base, id],
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Sucesso ao atualizar o registro!'
                );

                queryClient.refetchQueries(QUERIES_ADMIN.USER_TAG.base);
                queryClient.refetchQueries([QUERIES_ADMIN.USER_TAG.base, id]);
            },
            onError: (err) => {
                onErrorMessage(titleMessages, err);
            },
        }
    );

    const onSubmit = async () => {
        const data = {
            nome,
            slug,
        };

        const shapeSchema = {
            slug: Yup.string().nullable().required('O slug é obrigatório.'),
            nome: Yup.string().nullable().required('O nome é obrigatório.'),
        };

        const { success } = await onValidationSchema(shapeSchema, data);

        if (success) {
            if (!id) {
                mutateCreate(data);
            } else {
                mutateUpdate(data);
            }
        }
    };

    return (
        <Layout
            titlePage="Painel Administrativo"
            breadcrumbs={[
                {
                    label: 'Home',
                    href: getLinkWithSlug(ROUTES_ACCOUNT.SELECTED.fullPath),
                },
                {
                    label: 'Tags de Pessoa',
                    href: getLinkWithSlug(ROUTES_ADMIN.USER_TAG.fullPath),
                },
                {
                    label: 'Formulário Tags de Pessoa',
                    href: getLinkWithSlug(ROUTES_ADMIN.USER_TAG.FORM.fullPath),
                },
            ]}
            title="Formulário Tags de Pessoa"
            buttonTitle={
                <Button color="denary" onClick={() => onSubmit()} loading={isLoadingGet || isLoadingUpdate || isLoadingCreate}>
                    <AiFillSave />
                    Salvar
                </Button>
            }
        >
            <PersonTagFormWrap className="administrator-panel-user-tag-form">
                <Shadow>
                    <Loading active={isLoadingGet || isLoadingUpdate || isLoadingCreate} />

                    <OptionBlock
                        active={step}
                        onHandleActive={(param) => setStep(param)}
                        data={[
                            {
                                label: 'Informações Gerais',
                                value: 'GENERAL_INFO',
                            },
                        ]}
                        style={{ margin: '0 auto 0 0' }}
                    />

                    <InputText
                        model="sinary"
                        id="nome"
                        required
                        label="Nome"
                        name="nome"
                        placeholder="Informe o nome"
                        value={nome}
                        onChange={setNome}
                    />

                    <InputText
                        model="sinary"
                        id="slug"
                        required
                        label="Slug"
                        name="slug"
                        placeholder="Informe o slug, deve ser único"
                        value={slug}
                        onChange={setSlug}
                    />
                </Shadow>
            </PersonTagFormWrap>
        </Layout>
    );
};

export default PersonTagForm;
